import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { CheckCircle, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { listServiceCategoriesOptions } from '../../../features/service-categories/queries';
import { ServiceEditForm } from '../../../features/services/components/service-edit-form';
import { useApproveService, useRejectService } from '../../../features/services/mutations';
import { serviceByIdOptions } from '../../../features/services/queries';
import { PageHeader } from '../../../shared/components/page-header';

export const Route = createFileRoute('/_authenticated/services/$serviceId')({
  staticData: { breadcrumb: 'Редактирование' },
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(serviceByIdOptions(params.serviceId)),
      context.queryClient.ensureQueryData(listServiceCategoriesOptions()),
    ]),
  component: EditServicePage,
});

function EditServicePage() {
  const { serviceId } = Route.useParams();
  const { data: service } = useSuspenseQuery(serviceByIdOptions(serviceId));
  const { data: categories } = useSuspenseQuery(listServiceCategoriesOptions());

  const approve = useApproveService();
  const reject = useRejectService();
  const [rejectReason, setRejectReason] = useState('');

  const handleApprove = async () => {
    try {
      await approve.mutateAsync(serviceId);
      toast.success('Услуга одобрена и опубликована');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось одобрить');
    }
  };

  const handleReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    try {
      await reject.mutateAsync({ id: serviceId, reason });
      toast.success('Услуга отклонена');
      setRejectReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось отклонить');
    }
  };

  return (
    <div>
      <PageHeader
        title={service.title}
        description={service.provider?.username ? `Исполнитель: @${service.provider.username}` : undefined}
      />

      {service.status === 'PENDING_MODERATION' && (
        <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 p-4 mb-6 space-y-3">
          <div className="font-semibold text-amber-900 dark:text-amber-200">Ожидает модерации</div>
          <div className="flex flex-wrap gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="default" size="sm" disabled={approve.isPending}>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Одобрить
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Одобрить услугу?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Услуга будет опубликована и станет видна пользователям.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction onClick={handleApprove} disabled={approve.isPending}>
                    {approve.isPending ? 'Одобрение…' : 'Одобрить'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" disabled={reject.isPending}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Отклонить
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Отклонить услугу?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Услуга будет отклонена, исполнитель получит уведомление с причиной. Монеты будут
                    возвращены.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div>
                  <textarea
                    rows={3}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Причина отказа (обязательно)"
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel onClick={() => setRejectReason('')}>Отмена</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleReject}
                    disabled={reject.isPending || rejectReason.trim().length === 0}
                  >
                    {reject.isPending ? 'Отклонение…' : 'Отклонить'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      )}

      {service.status === 'ARCHIVED' && service.rejectionReason && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 mb-6 space-y-2">
          <div className="font-semibold text-destructive">Отклонено</div>
          <div className="text-sm">{service.rejectionReason}</div>
        </div>
      )}

      <ServiceEditForm service={service} categories={categories} />
    </div>
  );
}
